import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { SessionService } from "./session.service";

/**
 * Servicio que programa dos señales en base al campo `expiracion` de la sesión:
 *  - `sessionExpiring$`: pre-aviso (por defecto 60s antes de expirar).
 *  - `sessionExpired$`: instante en que la sesión caduca.
 *
 * La fuente de verdad es `SessionService.sesion$`: cualquier cambio de sesión
 * (login, refresh, logout) reprograma los timeouts automáticamente.
 */
@Injectable({ providedIn: "root" })
export class SessionTimerService implements OnDestroy {
	/** Emite como pre-aviso cuando la sesión está a punto de expirar. */
	sessionExpiring$ = new Subject<void>();

	/** Emite cuando la sesión ya expiró. */
	sessionExpired$ = new Subject<void>();

	/** Anticipación del pre-aviso (ms). */
	private readonly preavisoMs = 60 * 1000;

	private expiringTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private expiredTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private sesionSub: Subscription | null = null;

	constructor(
		private sessionService: SessionService,
		private ngZone: NgZone
	) {}

	/**
	 * Observa la sesión activa y reprograma los timeouts cada vez que cambia.
	 */
	watchSession(): void {
		this.sesionSub?.unsubscribe();
		this.sesionSub = this.sessionService.sesion$.subscribe((sesion) => {
			this.stop();
			if (sesion?.expiracion) {
				this.programar(new Date(sesion.expiracion).getTime());
			}
		});
	}

	/** Cancela cualquier timeout pendiente sin emitir eventos. */
	stop(): void {
		if (this.expiringTimeoutId !== null) {
			clearTimeout(this.expiringTimeoutId);
			this.expiringTimeoutId = null;
		}
		if (this.expiredTimeoutId !== null) {
			clearTimeout(this.expiredTimeoutId);
			this.expiredTimeoutId = null;
		}
	}

	ngOnDestroy(): void {
		this.stop();
		this.sesionSub?.unsubscribe();
	}

	private programar(expiracionMs: number): void {
		const ahora = Date.now();
		const msHastaExpirar = expiracionMs - ahora;

		// Ya expiró
		if (msHastaExpirar <= 0) {
			this.ngZone.run(() => this.sessionExpired$.next());
			return;
		}

		const msHastaPreaviso = msHastaExpirar - this.preavisoMs;

		this.ngZone.runOutsideAngular(() => {
			// Pre-aviso: solo si todavía queda tiempo para el preaviso.
			// Si el preaviso ya "pasó" (estamos dentro de la ventana), lo emitimos ya.
			if (msHastaPreaviso > 0) {
				this.expiringTimeoutId = setTimeout(() => {
					this.ngZone.run(() => this.sessionExpiring$.next());
				}, msHastaPreaviso);
			} else {
				this.ngZone.run(() => this.sessionExpiring$.next());
			}

			// Expiración real
			this.expiredTimeoutId = setTimeout(() => {
				this.ngZone.run(() => this.sessionExpired$.next());
			}, msHastaExpirar);
		});
	}
}
